import { STAGES } from "../components/common/ComplianceLifecycleWheel";

const STORE_KEY = "onedpdp_journey_v1";

function loadStore(userKey) {
  try {
    const raw = localStorage.getItem(`${STORE_KEY}_${userKey}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStore(userKey, store) {
  try {
    localStorage.setItem(`${STORE_KEY}_${userKey}`, JSON.stringify(store));
  } catch {
    // ignore
  }
}

export function formatJourneyDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatDuration(ms) {
  if (ms == null || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "< 1 min";
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 48) {
    return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
  }
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
}

export function syncModuleJourney({ progress = {}, apiTimestamps = {}, userKey = "default" }) {
  const store = loadStore(userKey);
  const now = new Date().toISOString();

  for (const stage of STAGES) {
    const stageId = stage.id;
    const pct = progress[stageId] ?? 0;
    const api = apiTimestamps[stageId] || {};

    if (!store[stageId]) store[stageId] = {};

    const entry = store[stageId];

    if (pct > 0 && !entry.startedAt) {
      entry.startedAt = api.firstActivityAt || api.lastUsedAt || now;
    } else if (pct > 0 && api.firstActivityAt) {
      // Prefer earliest known start from submissions API
      const apiStart = new Date(api.firstActivityAt).getTime();
      const stored = entry.startedAt ? new Date(entry.startedAt).getTime() : Infinity;
      if (apiStart < stored) entry.startedAt = api.firstActivityAt;
    }

    if (pct >= 100) {
      const doneAt = api.completedAt || api.lastUsedAt || now;
      if (!entry.completedAt) {
        entry.completedAt = doneAt;
      }
    } else if (pct === 0) {
      entry.completedAt = null;
    }

    entry.progress = pct;
    entry.updatedAt = now;
  }

  saveStore(userKey, store);
  return buildTimeline(store);
}

function buildTimeline(store) {
  return STAGES.map((stage, orderIndex) => {
    const entry = store[stage.id] || {};
    const pct = entry.progress ?? 0;
    let status = "pending";
    if (pct >= 100) status = "complete";
    else if (pct > 0) status = "in_progress";

    const startedAt = entry.startedAt || null;
    const completedAt = entry.completedAt || null;
    let durationMs = null;
    let durationLabel = "—";

    if (startedAt && completedAt) {
      durationMs = new Date(completedAt) - new Date(startedAt);
      durationLabel = formatDuration(durationMs);
    } else if (startedAt && status === "in_progress") {
      durationMs = Date.now() - new Date(startedAt);
      durationLabel = `${formatDuration(durationMs)} elapsed`;
    }

    return {
      id: stage.id,
      orderIndex,
      label: stage.label,
      short: stage.short,
      moduleKey: stage.short.toUpperCase(),
      color: stage.color,
      route: stage.route,
      progress: pct,
      status,
      startedAt,
      completedAt,
      durationMs,
      durationLabel,
    };
  });
}

export function getJourneyNarrative({ orgName, completedCount, totalStages, complianceScore }) {
  const org = orgName || "Your organization";
  if (completedCount >= totalStages) {
    return `${org} has reached full DPDP readiness across all ${totalStages} compliance modules. Your policies, assessments, and monitoring controls form a complete enterprise privacy program.`;
  }
  if (completedCount === 0) {
    return `${org} is embarking on its Digital Personal Data Protection Act journey. Start with Discover & Assess to establish your baseline, then progress through each module to build a defensible compliance story.`;
  }
  const score = complianceScore != null ? `${Math.round(complianceScore)}% overall progress` : "steady progress";
  const remaining = totalStages - completedCount;
  return `${org} has completed ${completedCount} of ${totalStages} modules (${score}). ${remaining} module${remaining === 1 ? "" : "s"} remain to achieve full enterprise-grade DPDP compliance.`;
}
